import React, {useEffect, useMemo, useRef} from "react";
import Icon from "./ui/Icon";
import {getCurrentTurnFromFen} from "../utils/chessFunctions";
import {DEFAULT_POSITION} from "chess.js";

/**
 * Clickable move list.
 *  - moves: SAN moves of the main line
 *  - initialFen: start position (black might start in jumpstart games)
 *  - currentPly: how many main line moves are applied in the shown position (0 = start)
 *  - onSelect(ply): jump to a position
 *  - ratings: optional array with a rating category per move (analysis)
 *  - variation: optional {fromPly, moves, current} for lines the user explored away from the game
 *  - onNavigate(direction): optional override for the arrow buttons ("first" | "prev" | "next" | "last")
 *  - extraControls: extra buttons shown next to the arrows
 */
const Moves = ({
                   moves,
                   initialFen,
                   currentPly,
                   onSelect,
                   ratings,
                   variation,
                   onClearVariation,
                   onNavigate,
                   canPrev,
                   canNext,
                   extraControls,
                   emptyText = "No moves yet. Make the first one!"
               }) => {
    const listRef = useRef(null);
    const startFen = initialFen || DEFAULT_POSITION;

    const rows = useMemo(() => {
        const startsWithBlack = getCurrentTurnFromFen(startFen) === "b";
        const fullMove = parseInt(startFen.split(" ")[5], 10) || 1;
        const result = [];
        let index = 0;
        let number = fullMove;
        if (startsWithBlack && moves.length > 0) {
            result.push({number, white: null, black: {san: moves[0], ply: 1}});
            index = 1;
            number++;
        }
        for (; index < moves.length; index += 2) {
            result.push({
                number,
                white: {san: moves[index], ply: index + 1},
                black: index + 1 < moves.length ? {san: moves[index + 1], ply: index + 2} : null
            });
            number++;
        }
        return result;
    }, [moves, startFen]);

    // Keep the current move in view
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        const current = list.querySelector(".move-cell.current");
        if (current) {
            const top = current.offsetTop - list.offsetTop;
            if (top < list.scrollTop || top > list.scrollTop + list.clientHeight - 40) {
                list.scrollTop = top - list.clientHeight / 2;
            }
        } else if (currentPly === moves.length) {
            list.scrollTop = list.scrollHeight;
        }
    }, [currentPly, moves.length, variation]);

    const inVariation = variation && variation.moves.length > 0;

    const renderCell = (cell) => {
        if (!cell) return <span className="move-cell" aria-hidden="true">{" "}</span>;
        const rating = ratings?.[cell.ply - 1];
        const isCurrent = !inVariation && cell.ply === currentPly;
        return (
            <button
                type="button"
                className={`move-cell ${isCurrent ? "current" : ""}`}
                onClick={() => onSelect && onSelect(cell.ply)}
                title={rating ? rating.category.title : undefined}
            >
                <span>{cell.san}</span>
                {rating && rating.category.showInList && (
                    <span className="move-cell__badge">{rating.category.symbol(15)}</span>
                )}
            </button>
        );
    };

    const navigate = (direction) => {
        if (onNavigate) return onNavigate(direction);
        if (!onSelect) return;
        if (direction === "first") onSelect(0);
        if (direction === "prev") onSelect(Math.max(0, currentPly - 1));
        if (direction === "next") onSelect(Math.min(moves.length, currentPly + 1));
        if (direction === "last") onSelect(moves.length);
    };

    const prevEnabled = canPrev !== undefined ? canPrev : currentPly > 0;
    const nextEnabled = canNext !== undefined ? canNext : currentPly < moves.length;

    return (
        <div className="moves">
            <div className="moves__list" ref={listRef}>
                {rows.length === 0 && <div className="moves__empty">{emptyText}</div>}
                {rows.map((row) => (
                    <div className="moves__row" key={row.number}>
                        <span className="moves__num">{row.number}.</span>
                        {renderCell(row.white)}
                        {renderCell(row.black)}
                    </div>
                ))}
                {inVariation && (
                    <div className="moves__variation">
                        <div className="moves__variation-title">
                            <span>Your line from move {Math.floor(variation.fromPly / 2) + 1}</span>
                            {onClearVariation && (
                                <button className="link-btn" onClick={onClearVariation}>Back to game</button>
                            )}
                        </div>
                        <div className="moves__variation-moves">
                            {variation.moves.map((san, i) => (
                                <span key={i} className={i === variation.moves.length - 1 ? "current" : ""}>{san}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div className="moves__nav">
                <button className="icon-btn" onClick={() => navigate("first")} disabled={!prevEnabled}
                        aria-label="First move" title="First move (Home)">
                    <Icon name="chevronsLeft"/>
                </button>
                <button className="icon-btn" onClick={() => navigate("prev")} disabled={!prevEnabled}
                        aria-label="Previous move" title="Previous move (←)">
                    <Icon name="chevronLeft"/>
                </button>
                <button className="icon-btn" onClick={() => navigate("next")} disabled={!nextEnabled}
                        aria-label="Next move" title="Next move (→)">
                    <Icon name="chevronRight"/>
                </button>
                <button className="icon-btn" onClick={() => navigate("last")} disabled={!nextEnabled}
                        aria-label="Latest move" title="Latest move (End)">
                    <Icon name="chevronsRight"/>
                </button>
                {extraControls}
            </div>
        </div>
    );
}

export default Moves;
